import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Select from 'react-select';
import { format, parse } from 'date-fns';
import { toast } from 'react-toastify';
import { useAppContext } from '../../App';
import { Row, Container, Col, Form, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { FaInfoCircle, FaPlus, FaMinus, FaTrash, FaEdit, FaChevronLeft, FaChevronRight, FaChevronDown, FaBroom, FaQuestionCircle, FaPlayCircle, FaPauseCircle, FaCalculator, FaDollarSign } from "react-icons/fa";
import styles from './TradingStrategyConfig.module.css';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import TimePicker from 'react-time-picker';
import 'react-time-picker/dist/TimePicker.css';
import { TextField } from '@mui/material';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { getInstrumentBySymbol, getMarginRequirements, getAllInstruments } from '../../constants/tradingInstruments';

// Validation schema
const schema = yup.object().shape({
  name: yup.string().required('Strategy name is required'),
  executionDays: yup.array().min(1, 'Select at least one execution day'),
  executionStartDate: yup.date().required('Start date is required'),
  executionEndDate: yup.date()
    .required('End date is required')
    .min(yup.ref('executionStartDate'), 'End date must be after start date'),
  executionTime: yup.string().required('Execution time is required'),
  timeZone: yup.string().required('Time zone is required'),
  security: yup.string().required('Security is required'),
  direction: yup.string().required('Direction is required'),
  totalContracts: yup.number()
    .required('Total contracts is required')
    .min(1, 'Must be at least 1 contract')
    .max(100, 'Maximum 100 contracts allowed'),
  series: yup.array().of(
    yup.object().shape({
      contracts: yup.number()
        .required('Contracts required')
        .min(1, 'Must be at least 1 contract'),
      takeProfit: yup.number()
        .required('Take profit required')
        .min(1, 'Must be at least 1 tick'),
      stopLoss: yup.number()
        .required('Stop loss required')
        .min(1, 'Must be at least 1 tick')
    })
  ).min(1, 'At least one series is required')
});

const DAYS = [
  { value: 'MON', label: 'Monday' },
  { value: 'TUE', label: 'Tuesday' },
  { value: 'WED', label: 'Wednesday' },
  { value: 'THU', label: 'Thursday' },
  { value: 'FRI', label: 'Friday' },
  { value: 'SAT', label: 'Saturday' },
  { value: 'SUN', label: 'Sunday' }
];

const TIMEZONES = [
  { value: 'ET', label: 'Eastern Time' },
  { value: 'PT', label: 'Pacific Time' },
  { value: 'CT', label: 'Central Time' },
  { value: 'MT', label: 'Mountain Time' }
];

// Map the original securities to constants for margin calculations
const SECURITY_MAPPING = {
  'MESU5': 'MES', // Micro E-mini S&P 500
  'MNQU5': 'MNQ', // Micro E-mini NASDAQ-100
  'RTYU5': 'RTY', // E-mini Russell 2000
  'M2KU5': 'M2K'  // Micro E-mini Russell 2000
};

const SECURITIES = [
  { value: 'MESU5', label: 'Micro E-mini S&P 500 (MESU5)' },
  { value: 'MNQU5', label: 'Micro E-mini NASDAQ (MNQU5)' },
  { value: 'RTYU5', label: 'E-mini Russell 2000 (RTYU5)' },
  { value: 'M2KU5', label: 'Micro E-mini Russell 2000 (M2kU5)' },
];

// Margin Requirements and Profitability Calculator Component
const MarginCalculator = ({ strategy, strategyIndex }) => {
  // Map the strategy security to the constants symbol
  const mappedSymbol = SECURITY_MAPPING[strategy.security] || strategy.security;
  const instrument = getInstrumentBySymbol(mappedSymbol);
  const totalContracts = strategy.series.reduce((total, series) => total + (series.contracts || 1), 0);
  
  if (!instrument) {
    return null;
  }

  // Calculate total margin requirements
  const dayMargin = parseFloat(instrument.dayMargin.replace(/[$,]/g, '')) || 0;
  const swingMargin = parseFloat(instrument.swingMargin.replace(/[$,]/g, '')) || 0;
  const totalDayMargin = dayMargin * totalContracts;
  const totalSwingMargin = swingMargin * totalContracts;

  // Calculate potential profit/loss for each series
  const calculateSeriesPL = (series) => {
    const contracts = series.contracts || 1;
    const takeProfit = series.takeProfit || 0;
    const stopLoss = series.stopLoss || 0;
    
    // Assuming $0.25 tick size for most futures (this would need to be instrument-specific)
    const tickSize = 0.25;
    const tickValue = 12.50; // Standard tick value for most futures
    
    const potentialProfit = takeProfit * tickValue * contracts;
    const potentialLoss = stopLoss * tickValue * contracts;
    
    return { potentialProfit, potentialLoss };
  };

  const totalPotentialProfit = strategy.series.reduce((total, series) => {
    const { potentialProfit } = calculateSeriesPL(series);
    return total + potentialProfit;
  }, 0);

  const totalPotentialLoss = strategy.series.reduce((total, series) => {
    const { potentialLoss } = calculateSeriesPL(series);
    return total + potentialLoss;
  }, 0);

  return (
    <div className={styles.marginCalculator} style={{
      background: 'rgba(0, 204, 0, 0.1)',
      border: '1px solid var(--color-success)',
      borderRadius: '8px',
      padding: '16px',
      marginTop: '16px',
      marginBottom: '16px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
        <FaCalculator style={{ marginRight: '8px', color: 'var(--color-success)' }} />
        <h4 style={{ margin: 0, color: 'var(--color-success)' }}>Margin Requirements & Profitability Analysis</h4>
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
        {/* Margin Requirements */}
        <div>
          <h5 style={{ color: 'var(--color-text)', marginBottom: '8px' }}>Margin Requirements</h5>
          <div style={{ background: 'var(--color-bg-panel)', padding: '12px', borderRadius: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ color: 'var(--color-text-muted)' }}>Day Margin:</span>
              <span style={{ color: 'var(--color-text)', fontWeight: 'bold' }}>${totalDayMargin.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ color: 'var(--color-text-muted)' }}>Swing Margin:</span>
              <span style={{ color: 'var(--color-text)', fontWeight: 'bold' }}>${totalSwingMargin.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ color: 'var(--color-text-muted)' }}>Total Contracts:</span>
              <span style={{ color: 'var(--color-text)', fontWeight: 'bold' }}>{totalContracts}</span>
            </div>
          </div>
        </div>

        {/* Profitability Analysis */}
        <div>
          <h5 style={{ color: 'var(--color-text)', marginBottom: '8px' }}>Profitability Analysis</h5>
          <div style={{ background: 'var(--color-bg-panel)', padding: '12px', borderRadius: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ color: 'var(--color-success)' }}>Max Profit:</span>
              <span style={{ color: 'var(--color-success)', fontWeight: 'bold' }}>${totalPotentialProfit.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ color: 'var(--color-danger)' }}>Max Loss:</span>
              <span style={{ color: 'var(--color-danger)', fontWeight: 'bold' }}>${totalPotentialLoss.toLocaleString()}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ color: 'var(--color-text-muted)' }}>Risk/Reward:</span>
              <span style={{ color: 'var(--color-text)', fontWeight: 'bold' }}>
                {totalPotentialLoss > 0 ? (totalPotentialProfit / totalPotentialLoss).toFixed(2) : 'N/A'} : 1
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Series Breakdown */}
      {strategy.series.length > 1 && (
        <div style={{ marginTop: '16px' }}>
          <h5 style={{ color: 'var(--color-text)', marginBottom: '8px' }}>Series Breakdown</h5>
          <div style={{ display: 'grid', gap: '8px' }}>
            {strategy.series.map((series, seriesIndex) => {
              const { potentialProfit, potentialLoss } = calculateSeriesPL(series);
              return (
                <div key={seriesIndex} style={{
                  background: 'var(--color-bg-panel)',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ color: 'var(--color-text-muted)' }}>Series {seriesIndex + 1}:</span>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <span style={{ color: 'var(--color-text-muted)' }}>{series.contracts} contracts</span>
                    <span style={{ color: 'var(--color-success)' }}>TP: ${potentialProfit.toLocaleString()}</span>
                    <span style={{ color: 'var(--color-danger)' }}>SL: ${potentialLoss.toLocaleString()}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const TradingStrategyConfig = () => {
  const {
    tradingStrategy,
    setTradingStrategy,
    loading,
    setLoading,
    message,
    setMessage,
    user,
    refreshUser
  } = useAppContext();

  const [isMobile, setIsMobile] = useState(false);
  const [nextExecution, setNextExecution] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [existingStrategies, setExistingStrategies] = useState([]);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [strategies, setStrategies] = useState([{
    name: '',
    executionDays: [],
    executionStartDate: new Date(),
    executionEndDate: new Date(),
    executionTime: '09:30:00',
    timeZone: 'ET',
    security: '',
    direction: 'BUY',
    totalContracts: 1,
    series: [{ contracts: 1, takeProfit: 10, stopLoss: 5 }]
  }]);

  // Add state for help audio
  const [isHelpPlaying, setIsHelpPlaying] = useState(false);
  const helpAudioRef = useRef(null);

  // Add help audio URL
  const helpAudioUrl = 'https://deng-software-cms-clients.s3.amazonaws.com/target_video_files/simple_tts/1751225288601.mp3';

  // Add help audio handlers
  const handleHelpAudioPlay = () => {
    if (!helpAudioRef.current) {
      // Create audio element if it doesn't exist
      helpAudioRef.current = new Audio(helpAudioUrl);
      helpAudioRef.current.addEventListener('ended', () => {
        setIsHelpPlaying(false);
      });
      helpAudioRef.current.addEventListener('error', () => {
        console.error('Error playing help audio');
        setIsHelpPlaying(false);
      });
    }

    if (isHelpPlaying) {
      // Stop the help audio
      helpAudioRef.current.pause();
      helpAudioRef.current.currentTime = 0;
      setIsHelpPlaying(false);
    } else {
      // Play the help audio
      helpAudioRef.current.play().then(() => {
        setIsHelpPlaying(true);
      }).catch((error) => {
        console.error('Error playing help audio:', error);
        setIsHelpPlaying(false);
      });
    }
  };

  // Cleanup help audio on unmount
  useEffect(() => {
    return () => {
      if (helpAudioRef.current) {
        helpAudioRef.current.pause();
        helpAudioRef.current = null;
      }
    };
  }, []);

  const { register, handleSubmit, control, watch, setValue, formState: { errors }, reset, trigger } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      executionDays: [],
      executionStartDate: new Date(),
      executionEndDate: new Date(),
      executionTime: '09:30:00',
      timeZone: 'ET',
      security: '',
      direction: 'BUY',
      totalContracts: 1,
      series: [{ contracts: 1, takeProfit: 10, stopLoss: 5 }]
    },
    mode: 'onChange'
  });

  // Watch total contracts for validation
  const totalContracts = watch('totalContracts');
  const series = watch('series');

  const [calendarOpen, setCalendarOpen] = useState(Array(strategies.length).fill({ start: false, end: false }));
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [strategyToEdit, setStrategyToEdit] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editCalendarOpen, setEditCalendarOpen] = useState({ start: false, end: false });
  const [cleanupModalOpen, setCleanupModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const strategiesPerPage = 5;
  const [showStrategies, setShowStrategies] = useState(existingStrategies.length > 0);

  // Initialize form values when strategies change
  useEffect(() => {
    if (strategies.length > 0) {
      const currentStrategy = strategies[0];
      Object.entries(currentStrategy).forEach(([key, value]) => {
        setValue(key, value, { shouldValidate: true });
      });
    }
  }, [strategies, setValue]);

  // Add effect to validate form on strategy changes
  useEffect(() => {
    if (strategies.length > 0) {
      const currentStrategy = strategies[0];
      // Set default values if they're empty
      if (!currentStrategy.name) {
        setValue('name', '', { shouldValidate: true });
      }
      if (!currentStrategy.executionDays || currentStrategy.executionDays.length === 0) {
        setValue('executionDays', [], { shouldValidate: true });
      }
      if (!currentStrategy.security) {
        setValue('security', '', { shouldValidate: true });
      }
    }
  }, [strategies, setValue]);

  // Add detailed validation logging
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      console.log('Form validation errors:', errors);
      console.log('Current form values:', watch());
      console.log('Current strategies state:', strategies);
    }
  }, [errors, watch, strategies]);

  // Check for mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Calculate next execution time
  useEffect(() => {
    const calculateNextExecution = () => {
      const now = new Date();
      const executionTime = watch('executionTime');
      const executionDays = watch('executionDays');
      
      if (executionTime && executionDays.length > 0) {
        setNextExecution(now);
      }
    };
    
    calculateNextExecution();
    const timer = setInterval(calculateNextExecution, 1000);
    
    return () => clearInterval(timer);
  }, [watch]);

  // Fetch existing strategies on component mount
  useEffect(() => {
    const fetchExistingStrategies = async () => {
      try {
        setLoading(true);
        console.log('Fetching strategies for user ID:', user?.username);
        const response = await fetch('https://vidplay-api.deng-software.com/flask/trading-strategy/get-strategies', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId: user?.username }),
        });
        if (!response.ok) {
          if (response.status === 404) {
            console.log('No strategies found yet');
            setExistingStrategies([]);
            return;
          }
          throw new Error(`Failed to fetch strategies: ${response.statusText}`);
        }
        const data = await response.json();
        setExistingStrategies(data);
      } catch (error) {
        console.error('Error fetching strategies:', error);
        toast.error(error.message || 'Failed to load existing strategies');
        setExistingStrategies([]);
      } finally {
        setLoading(false);
    }
    };

    if (user?.username) {
      fetchExistingStrategies();
    }
  }, [user]);

  useEffect(() => {
    if (existingStrategies.length > 0) {
      setShowStrategies(true);
    }
  }, [existingStrategies.length]);

  const toggleExpand = () => {
    setExpanded(prev => !prev);
  };

  const addStrategy = () => {
    setStrategies(prev => [...prev, {
      name: '',
      executionDays: [],
      executionStartDate: new Date(),
      executionEndDate: new Date(),
      executionTime: '09:30:00',
      timeZone: 'ET',
      security: '',
      direction: 'BUY',
      totalContracts: 1,
      series: [{ contracts: 1, takeProfit: 10, stopLoss: 5 }]
    }]);
  };

  const removeStrategy = (index) => {
    setStrategies(prev => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (formData) => {
    console.log('Form submitted with data:', formData);
    console.log('Submitting strategies for user ID:', user?.username);
    try {
      setLoading(true);
      setMessage('Sending strategy to gateway...');
  
      const savePromises = strategies.map(async (strategy) => {
        const formattedStrategy = {
          ...strategy,
          executionStartDate: format(new Date(strategy.executionStartDate), 'yyyy-MM-dd'),
          executionEndDate: format(new Date(strategy.executionEndDate), 'yyyy-MM-dd'),
          userId: user?.username
        };
  
        // Ensure execution time is in HH:mm:ss format
        if (formattedStrategy.executionTime) {
          const timeParts = formattedStrategy.executionTime.split(':');
          if (timeParts.length === 2) {
            // Only HH:mm format, add seconds
            formattedStrategy.executionTime = `${formattedStrategy.executionTime}:00`;
          }
        }
  
        console.log('Sending strategy to API:', formattedStrategy);
  
        const response = await fetch('https://vidplay-api.deng-software.com/flask/trading-strategy', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formattedStrategy),
        });
  
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(errorData?.message || `Gateway error: ${response.status}`);
        }
  
        return response.json();
      });
  
      const results = await Promise.all(savePromises);
      setSubmissionResult(results);
      setTradingStrategy(strategies);
      toast.success('Strategies scheduled successfully via gateway!');
      setMessage('Strategies scheduled successfully!');
      
      // Refresh existing strategies after successful submission
      console.log('Refreshing strategies for user ID:', user?.username);
      const refreshResponse = await fetch('https://vidplay-api.deng-software.com/flask/trading-strategy/get-strategies', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId: user?.username }),
      });
      if (refreshResponse.ok) {
        const updatedStrategies = await refreshResponse.json();
        setExistingStrategies(updatedStrategies);
      }
    } catch (error) {
      console.error('Submit Error:', error);
      toast.error(error.message || 'Failed to schedule strategy');
      setMessage(error.message || 'Error scheduling strategy');
    } finally {
      setLoading(false);
    }
  };
  

  const addSeries = (strategyIndex) => {
    const currentStrategy = strategies[strategyIndex];
    if (currentStrategy.series.length < 5) {
      const updatedStrategies = [...strategies];
      const newSeries = {
        contracts: 1,
        takeProfit: 10,
        stopLoss: 5
      };
      updatedStrategies[strategyIndex].series.push(newSeries);
      setStrategies(updatedStrategies);
      setValue(`strategies.${strategyIndex}.series`, updatedStrategies[strategyIndex].series);
    }
  };

  const removeSeries = (strategyIndex, seriesIndex) => {
    const updatedStrategies = [...strategies];
    updatedStrategies[strategyIndex].series = updatedStrategies[strategyIndex].series.filter((_, i) => i !== seriesIndex);
    setStrategies(updatedStrategies);
    setValue(`strategies.${strategyIndex}.series`, updatedStrategies[strategyIndex].series);
  };

  // Open edit modal and set form
  const handleEdit = (strategy) => {
    console.log('Opening edit modal with strategy:', strategy);
    console.log('Strategy date fields:');
    console.log('- executionStartDate:', strategy.executionStartDate);
    console.log('- executionEndDate:', strategy.executionEndDate);
    console.log('- executionTime:', strategy.executionTime);
    console.log('- executionTime type:', typeof strategy.executionTime);
    console.log('- executionTime length:', strategy.executionTime?.length);
    console.log('Strategy series data:', strategy.series);
    console.log('Series details:', strategy.series?.map((series, index) => ({
      index,
      contracts: series.contracts,
      takeProfit: series.takeProfit,
      stopLoss: series.stopLoss
    })));
    
    setStrategyToEdit(strategy);
    
    // Convert execution days from full names to short codes for the Select component
    const fullToShortMapping = {
      "Monday": "MON",
      "Tuesday": "TUE", 
      "Wednesday": "WED",
      "Thursday": "THU",
      "Friday": "FRI",
      "Saturday": "SAT",
      "Sunday": "SUN"
    };
    
    const formattedStrategy = {
      ...strategy,
      executionDays: (strategy.executionDays || []).map(day => fullToShortMapping[day] || day)
    };
    
    console.log('Formatted strategy for edit form:', formattedStrategy);
    setEditForm(formattedStrategy);
    setEditModalOpen(true);
  };

  // Handle edit form changes
  const handleEditFormChange = (field, value) => {
    console.log('handleEditFormChange called with field:', field, 'value:', value);
    setEditForm(prev => {
      const updated = { ...prev, [field]: value };
      console.log('Updated editForm:', updated);
      return updated;
    });
  };

  // Save edited strategy
  const handleSaveEdit = async () => {
    if (!strategyToEdit || !strategyToEdit._id) return;
    try {
      setLoading(true);
      
      // Convert execution days from short codes back to full names for the backend
      const shortToFullMapping = {
        "MON": "Monday",
        "TUE": "Tuesday",
        "WED": "Wednesday", 
        "THU": "Thursday",
        "FRI": "Friday",
        "SAT": "Saturday",
        "SUN": "Sunday"
      };
      
      const dataToSend = {
        ...editForm,
        executionDays: (editForm.executionDays || []).map(day => shortToFullMapping[day] || day),
        userId: user?.username
      };
      
      // Ensure execution time is in HH:mm:ss format
      if (dataToSend.executionTime) {
        const timeParts = dataToSend.executionTime.split(':');
        if (timeParts.length === 2) {
          // Only HH:mm format, add seconds
          dataToSend.executionTime = `${dataToSend.executionTime}:00`;
        }
      }
      
      // Ensure date fields are always sent as YYYY-MM-DD (local, not UTC)
      if (dataToSend.executionStartDate) {
        dataToSend.executionStartDate = format(new Date(dataToSend.executionStartDate), 'yyyy-MM-dd');
      }
      if (dataToSend.executionEndDate) {
        dataToSend.executionEndDate = format(new Date(dataToSend.executionEndDate), 'yyyy-MM-dd');
      }
      
      console.log('Sending data to backend:', dataToSend);
      console.log('Date fields being sent:');
      console.log('- executionStartDate:', dataToSend.executionStartDate);
      console.log('- executionEndDate:', dataToSend.executionEndDate);
      console.log('- executionTime:', dataToSend.executionTime);
      console.log('Series data being sent:', dataToSend.series);
      console.log('Series details:', dataToSend.series?.map((series, index) => ({
        index,
        contracts: series.contracts,
        takeProfit: series.takeProfit,
        stopLoss: series.stopLoss
      })));
      
      const response = await fetch(`https://vidplay-api.deng-software.com/flask/trading-strategy/${strategyToEdit._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || `Update failed: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('Update response:', result);
      
      toast.success('Strategy updated!');
      setEditModalOpen(false);
      setStrategyToEdit(null);
      // Refresh strategies
      const refreshResponse = await fetch('https://vidplay-api.deng-software.com/flask/trading-strategy/get-strategies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user?.username }),
      });
      if (refreshResponse.ok) {
        const updatedStrategies = await refreshResponse.json();
        setExistingStrategies(updatedStrategies);
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.message || 'Failed to update strategy');
    } finally {
      setLoading(false);
    }
  };

  // Delete strategy
  const handleDelete = async (strategy) => {
    if (!window.confirm('Are you sure you want to delete this strategy?')) return;
    setDeletingId(strategy._id);
    try {
      setLoading(true);
      const response = await fetch(`https://vidplay-api.deng-software.com/flask/trading-strategy/${strategy._id}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user?.username }),
      });
      if (!response.ok) throw new Error('Failed to delete strategy');
      toast.success('Strategy deleted!');
      // Refresh strategies
      const refreshResponse = await fetch('https://vidplay-api.deng-software.com/flask/trading-strategy/get-strategies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user?.username }),
      });
      if (refreshResponse.ok) {
        const updatedStrategies = await refreshResponse.json();
        setExistingStrategies(updatedStrategies);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete strategy');
    } finally {
      setLoading(false);
      setDeletingId(null);
    }
  };

  // Cleanup orphaned jobs and strategies
  const handleCleanup = async () => {
    setCleanupModalOpen(true);
  };

  const handleCleanupConfirm = async () => {
    setCleanupModalOpen(false);
    
    try {
      setLoading(true);
      const response = await fetch('https://vidplay-api.deng-software.com/flask/trading-strategy/cleanup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user?.username }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.error || `Cleanup failed: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        toast.success(`Cleanup completed for your account! ${result.summary.jobs_cleaned} jobs and ${result.summary.strategies_cleaned} strategies cleaned.`);
        
        // Show detailed results
        if (result.summary.orphaned_jobs_found > 0 || result.summary.orphaned_strategies_found > 0) {
          console.log('Cleanup details:', result.details);
        }
        
        // Refresh strategies after cleanup
        const refreshResponse = await fetch('https://vidplay-api.deng-software.com/flask/trading-strategy/get-strategies', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: user?.username }),
        });
        if (refreshResponse.ok) {
          const updatedStrategies = await refreshResponse.json();
          setExistingStrategies(updatedStrategies);
        }
      } else {
        throw new Error(result.error || 'Cleanup failed');
      }
    } catch (error) {
      console.error('Cleanup Error:', error);
      toast.error(error.message || 'Failed to run cleanup');
    } finally {
      setLoading(false);
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(existingStrategies.length / strategiesPerPage);
  const paginatedStrategies = existingStrategies.slice(
    (currentPage - 1) * strategiesPerPage,
    currentPage * strategiesPerPage
  );

  // Add loading state display
  if (loading) {
    return (
      <Container className={styles.container}>
        <div className={styles.loading}>
          <h2>Loading...</h2>
        </div>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
    <Container className={styles.container}>
      <div className={styles.header} style={{ position: 'relative' }}>
          <h1>Trading Strategy Configuration</h1>
          <button
            type="button"
            onClick={handleHelpAudioPlay}
            style={{
              position: 'absolute',
              top: '0',
              right: '0',
              background: isHelpPlaying ? '#ff6b35' : 'rgba(0, 204, 0, 0.2)',
              border: `2px solid ${isHelpPlaying ? '#ff6b35' : 'var(--color-success)'}`,
              color: isHelpPlaying ? 'var(--color-text)' : 'var(--color-success)',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '10px',
              borderRadius: '50%',
              transition: 'all 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              minWidth: '40px',
              minHeight: '40px',
              boxShadow: `0 0 8px ${isHelpPlaying ? '#ff6b35' : 'var(--color-success)'}`,
              textShadow: `0 0 6px ${isHelpPlaying ? '#ff6b35' : 'var(--color-success)'}`,
              animation: isHelpPlaying ? 'pulse 1.5s infinite' : 'none'
            }}
            onMouseEnter={(e) => {
              if (!isHelpPlaying) {
                e.target.style.backgroundColor = 'rgba(0, 204, 0, 0.4)';
                e.target.style.transform = 'scale(1.1)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isHelpPlaying) {
                e.target.style.backgroundColor = 'rgba(0, 204, 0, 0.2)';
                e.target.style.transform = 'scale(1)';
              }
            }}
            title={isHelpPlaying ? "Stop help audio" : "Play interface help audio"}
          >
            <FaQuestionCircle />
          </button>
        </div>
       

        {/* Display existing strategies */}
        {existingStrategies.length > 0 ? (
          <div className={styles.existingStrategies}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <Button color="link" onClick={() => setShowStrategies((prev) => !prev)} style={{ fontSize: 24, color: 'var(--color-text)', textDecoration: 'none' }}>
                  {showStrategies ? <FaChevronDown /> : <FaChevronRight />}
                </Button>
                <h2 style={{ marginBottom: 0 }}>View Existing Strategies</h2>
              </div>
              <Button 
                color="warning" 
                size="sm" 
                onClick={handleCleanup}
                disabled={loading}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  background: 'var(--color-warning)',
                  border: 'none',
                  color: 'var(--color-text-strong)',
                  fontWeight: 'bold'
                }}
                title="Clean up orphaned jobs and strategies"
              >
                <FaBroom />
                {loading ? 'Cleaning...' : 'Cleanup'}
              </Button>
            </div>
            {showStrategies && (
              <div className={styles.strategyGridWrapper}>
                {/* Header Row */}
                <Row className={styles.strategyHeaderRow}>
                  <Col>Name</Col>
                  <Col>Security</Col>
                  <Col>Execution Time</Col>
                  <Col>Total Contracts</Col>
                  <Col>Status</Col>
                  <Col>Next Run</Col>
                  <Col>Max Profit</Col>
                  <Col>Max Loss</Col>
                  <Col>Risk/Reward</Col>
                  <Col>Actions</Col>
                </Row>
                {/* Data Rows (paginated) */}
                {(() => {
                  // Calculate totals for roll-up
                  let totalContracts = 0;
                  let totalProfit = 0;
                  let totalLoss = 0;
                  
                  const strategyRows = paginatedStrategies.map((strategy, index) => {
                    // Calculate profit/loss and risk/reward for this strategy
                    const calculateStrategyPL = () => {
                      if (!strategy.series || !Array.isArray(strategy.series)) return { totalProfit: 0, totalLoss: 0, riskReward: 0 };
                      
                      const totalProfit = strategy.series.reduce((total, series) => {
                        const contracts = series.contracts || 1;
                        const takeProfit = series.takeProfit || 0;
                        const tickValue = 12.50; // Standard tick value for most futures
                        return total + (takeProfit * tickValue * contracts);
                      }, 0);
                      
                      const totalLoss = strategy.series.reduce((total, series) => {
                        const contracts = series.contracts || 1;
                        const stopLoss = series.stopLoss || 0;
                        const tickValue = 12.50; // Standard tick value for most futures
                        return total + (stopLoss * tickValue * contracts);
                      }, 0);
                      
                      const riskReward = totalLoss > 0 ? (totalProfit / totalLoss).toFixed(2) : 0;
                      
                      return { totalProfit, totalLoss, riskReward };
                    };
                    
                    const { totalProfit: strategyProfit, totalLoss: strategyLoss, riskReward } = calculateStrategyPL();
                    
                    // Add to roll-up totals
                    totalContracts += strategy.series.reduce((total, series) => total + (series.contracts || 1), 0);
                    totalProfit += strategyProfit;
                    totalLoss += strategyLoss;
                    
                    return (
                      <Row key={strategy._id || index} className={styles.strategyDataRow}>
                        <Col>{strategy.name}</Col>
                        <Col>{strategy.security}</Col>
                        <Col>{strategy.executionTime}</Col>
                        <Col>{strategy.series.reduce((total, series) => total + (series.contracts || 1), 0)}</Col>
                        <Col>{strategy.status}</Col>
                        <Col>{strategy.nextRunTime ? new Date(strategy.nextRunTime).toLocaleString() : ''}</Col>
                        <Col>
                          <span style={{ color: 'var(--color-success)', fontWeight: 'bold' }}>
                            +${strategyProfit.toLocaleString()}
                          </span>
                        </Col>
                        <Col>
                          <span style={{ color: 'var(--color-danger)', fontWeight: 'bold' }}>
                            -${strategyLoss.toLocaleString()}
                          </span>
                        </Col>
                        <Col>
                          <span style={{ 
                            color: riskReward >= 2 ? 'var(--color-success)' : riskReward >= 1.5 ? 'var(--color-warning)' : 'var(--color-danger)',
                            fontWeight: 'bold'
                          }}>
                            {riskReward}:1
                          </span>
                        </Col>
                        <Col>
                          <Button color="primary" size="sm" onClick={() => handleEdit(strategy)} style={{ marginRight: 8, padding: '4px 8px' }} title="Edit">
                            <FaEdit />
                          </Button>
                          <Button color="danger" size="sm" onClick={() => handleDelete(strategy)} disabled={deletingId === strategy._id} style={{ padding: '4px 8px' }} title="Delete">
                            <FaTrash />
                          </Button>
                        </Col>
                      </Row>
                    );
                  });
                  
                  // Add roll-up totals row
                  const totalRiskReward = totalLoss > 0 ? (totalProfit / totalLoss).toFixed(2) : 0;
                  
                  return (
                    <>
                      {strategyRows}
                      {/* Roll-up Totals Row */}
                      <Row className={`${styles.strategyDataRow} ${styles.totalsRow}`}>
                        <Col>TOTALS</Col>
                        <Col>{paginatedStrategies.length} Strategies</Col>
                        <Col>-</Col>
                        <Col>{totalContracts}</Col>
                        <Col>-</Col>
                        <Col>-</Col>
                        <Col>
                          <span style={{ color: 'var(--color-success)', fontWeight: 'bold', fontSize: '14px' }}>
                            +${totalProfit.toLocaleString()}
                          </span>
                        </Col>
                        <Col>
                          <span style={{ color: 'var(--color-danger)', fontWeight: 'bold', fontSize: '14px' }}>
                            -${totalLoss.toLocaleString()}
                          </span>
                        </Col>
                        <Col>
                          <span style={{ 
                            color: totalRiskReward >= 2 ? 'var(--color-success)' : totalRiskReward >= 1.5 ? 'var(--color-warning)' : 'var(--color-danger)',
                            fontWeight: 'bold',
                            fontSize: '14px'
                          }}>
                            {totalRiskReward}:1
                          </span>
                        </Col>
                        <Col>-</Col>
                      </Row>
                    </>
                  );
                })()}
                {/* Pagination Controls */}
                <div className={styles.paginationControls}>
                  <Button color="secondary" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} style={{ marginRight: 8, padding: '4px 8px' }}>
                    <FaChevronLeft />
                  </Button>
                  <span>Page {currentPage} of {totalPages}</span>
                  <Button color="secondary" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} style={{ marginLeft: 8, padding: '4px 8px' }}>
                    <FaChevronRight />
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className={styles.noStrategies}>
            <p>No strategies configured yet. Add your first strategy below.</p>
          </div>
        )}

        {/* Display submission result */}
        {submissionResult && Array.isArray(submissionResult) && submissionResult.length > 0 && (
          <div className={styles.submissionResult} style={{ margin: '16px 0' }}>
            <h2>Last Submission Result</h2>
            {/* Header Row */}
            <Row className={styles.strategyHeaderRow}>
              <Col xs="auto">Job ID</Col>
              <Col>Message</Col>
              <Col>Strategy ID</Col>
              <Col>Success</Col>
            </Row>
            {submissionResult.map((result, idx) => (
              <Row key={idx} className={styles.strategyDataRow}>
                <Col xs="auto">{result.jobId}</Col>
                <Col>{result.message}</Col>
                <Col>{result.strategyId}</Col>
                <Col>{result.success ? 'Yes' : 'No'}</Col>
              </Row>
            ))}
          </div>
        )}

        <Form 
          className={styles.form} 
          onSubmit={(e) => {
            e.preventDefault();
            console.log('Form submit event triggered');
            console.log('Current form values before submit:', watch());
            handleSubmit(
              (data) => {
                console.log('Form validation passed, submitting data:', data);
                onSubmit(data);
              },
              (errors) => {
                console.log('Form validation failed:', errors);
                console.log('Current form values:', watch());
                toast.error('Please fix the validation errors before submitting');
              }
            )(e);
          }}
        >
          {strategies.map((strategy, strategyIndex) => (
            <div key={strategyIndex} className={styles.strategyGroup}>
              <div className={styles.strategyHeader}>
                <h2>Strategy {strategyIndex + 1}</h2>
                {strategies.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeStrategy(strategyIndex)}
                    className={styles.removeStrategyButton}
                  >
                    <FaTrash /> Remove Strategy
                  </button>
                )}
      </div>

              <div className={styles.strategyContent}>
                <div className={styles.formRow}>
                <div className={styles.formGroup}>
                    <label>Strategy Name</label>
                  <Controller
                    name="name"
                    control={control}
                    defaultValue={strategy.name}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="text"
                        value={strategy.name}
                        onChange={(e) => {
                          const updatedStrategies = [...strategies];
                          updatedStrategies[strategyIndex].name = e.target.value;
                          setStrategies(updatedStrategies);
                          field.onChange(e.target.value);
                        }}
                        placeholder="Enter strategy name"
                      />
                    )}
                  />
                  {errors.name && (
                    <span className={styles.errorText}>
                      {errors.name.message}
                    </span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label>Time Zone</label>
                  <Controller
                    name="timeZone"
                    control={control}
                    defaultValue={strategy.timeZone}
                    render={({ field }) => (
                      <Select
                        classNamePrefix="select"
                        {...field}
                        options={TIMEZONES}
                        value={TIMEZONES.find(tz => tz.value === strategy.timeZone)}
                        onChange={(option) => {
                          const updatedStrategies = [...strategies];
                          updatedStrategies[strategyIndex].timeZone = option.value;
                          setStrategies(updatedStrategies);
                        }}
                      />
                    )}
                  />
                </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Execution Days</label>
                    <Controller
                      name="executionDays"
                      control={control}
                      defaultValue={strategy.executionDays}
                      render={({ field }) => (
                        <Select
                          classNamePrefix="select"
                          {...field}
                          options={DAYS}
                          isMulti
                          value={DAYS.filter(day => strategy.executionDays.includes(day.value))}
                          onChange={(options) => {
                            const updatedStrategies = [...strategies];
                            updatedStrategies[strategyIndex].executionDays = options.map(opt => opt.value);
                            setStrategies(updatedStrategies);
                            field.onChange(options.map(opt => opt.value));
                          }}
                        />
                      )}
                    />
                    {errors.executionDays && (
                      <span className={styles.errorText}>
                        {errors.executionDays.message}
                      </span>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label>Execution Start Date</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={strategy.executionStartDate ? format(new Date(strategy.executionStartDate), 'yyyy-MM-dd') : ''}
                      onFocus={() => {
                        const openArr = [...calendarOpen];
                        openArr[strategyIndex] = { ...openArr[strategyIndex], start: true };
                        setCalendarOpen(openArr);
                      }}
                      onChange={e => {
                        const updatedStrategies = [...strategies];
                        const parsed = parse(e.target.value, 'yyyy-MM-dd', new Date());
                        if (!isNaN(parsed)) {
                          updatedStrategies[strategyIndex].executionStartDate = parsed;
                          setStrategies(updatedStrategies);
                        }
                      }}
                      placeholder="YYYY-MM-DD"
                      readOnly={false}
                      style={{ background: 'var(--color-bg-alt)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 4, padding: '0.75rem', fontSize: '1rem' }}
                    />
                    {calendarOpen[strategyIndex]?.start && (
                      <div style={{ position: 'absolute', zIndex: 10, background: 'var(--color-bg-panel)', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                        <DayPicker
                          mode="single"
                          selected={strategy.executionStartDate ? new Date(strategy.executionStartDate) : undefined}
                          onSelect={date => {
                            // Always close the calendar when any date is selected
                            const openArr = [...calendarOpen];
                            openArr[strategyIndex] = { ...openArr[strategyIndex], start: false };
                            setCalendarOpen(openArr);
                            
                            if (date) {
                              const updatedStrategies = [...strategies];
                              updatedStrategies[strategyIndex].executionStartDate = date;
                              setStrategies(updatedStrategies);
                            }
                          }}
                          fromDate={new Date()}
                          styles={{
                            caption: { color: 'var(--color-text)' },
                            head_cell: { color: 'var(--color-text)' },
                            day_selected: { backgroundColor: 'var(--color-success)', color: 'var(--color-text-strong)' },
                            day: { color: 'var(--color-text)', background: 'transparent' },
                            months: { background: 'var(--color-bg-panel)', borderRadius: 8 },
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label>Execution End Date</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={strategy.executionEndDate ? format(new Date(strategy.executionEndDate), 'yyyy-MM-dd') : ''}
                      onFocus={() => {
                        const openArr = [...calendarOpen];
                        openArr[strategyIndex] = { ...openArr[strategyIndex], end: true };
                        setCalendarOpen(openArr);
                      }}
                      onChange={e => {
                        const updatedStrategies = [...strategies];
                        const parsed = parse(e.target.value, 'yyyy-MM-dd', new Date());
                        if (!isNaN(parsed)) {
                          updatedStrategies[strategyIndex].executionEndDate = parsed;
                          setStrategies(updatedStrategies);
                        }
                      }}
                      placeholder="YYYY-MM-DD"
                      readOnly={false}
                      style={{ background: 'var(--color-bg-alt)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 4, padding: '0.75rem', fontSize: '1rem' }}
                    />
                    {calendarOpen[strategyIndex]?.end && (
                      <div style={{ position: 'absolute', zIndex: 10, background: 'var(--color-bg-panel)', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                        <DayPicker
                          mode="single"
                          selected={strategy.executionEndDate ? new Date(strategy.executionEndDate) : undefined}
                          onSelect={date => {
                            // Always close the calendar when any date is selected
                            const openArr = [...calendarOpen];
                            openArr[strategyIndex] = { ...openArr[strategyIndex], end: false };
                            setCalendarOpen(openArr);
                            
                            if (date) {
                              const updatedStrategies = [...strategies];
                              updatedStrategies[strategyIndex].executionEndDate = date;
                              setStrategies(updatedStrategies);
                            }
                          }}
                          fromDate={strategy.executionStartDate ? new Date(strategy.executionStartDate) : new Date()}
                          styles={{
                            caption: { color: 'var(--color-text)' },
                            head_cell: { color: 'var(--color-text)' },
                            day_selected: { backgroundColor: 'var(--color-success)', color: 'var(--color-text-strong)' },
                            day: { color: 'var(--color-text)', background: 'transparent' },
                            months: { background: 'var(--color-bg-panel)', borderRadius: 8 },
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label>Execution Time</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={strategy.executionTime || ''}
                      onChange={(e) => {
                        const updatedStrategies = [...strategies];
                        updatedStrategies[strategyIndex].executionTime = e.target.value;
                        setStrategies(updatedStrategies);
                      }}
                      placeholder="HH:mm:ss"
                      style={{ background: 'var(--color-bg-alt)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 4, padding: '0.75rem', fontSize: '1rem' }}
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Security</label>
                    <Controller
                      name="security"
                      control={control}
                      defaultValue={strategy.security}
                      render={({ field }) => (
                        <Select
                          classNamePrefix="select"
                          {...field}
                          options={SECURITIES}
                          value={SECURITIES.find(sec => sec.value === strategy.security)}
                          onChange={(option) => {
                            const updatedStrategies = [...strategies];
                            updatedStrategies[strategyIndex].security = option.value;
                            setStrategies(updatedStrategies);
                            field.onChange(option.value);
                          }}
                        />
                      )}
                    />
                    {errors.security && (
                      <span className={styles.errorText}>
                        {errors.security.message}
                      </span>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label>Direction</label>
                      <div className={styles.radioGroup}>
                        <label>
                          <input
                            type="radio"
                          checked={strategy.direction === 'BUY'}
                          onChange={() => {
                            const updatedStrategies = [...strategies];
                            updatedStrategies[strategyIndex].direction = 'BUY';
                            setStrategies(updatedStrategies);
                          }}
                          />
                          BUY
                        </label>
                        <label>
                          <input
                            type="radio"
                          checked={strategy.direction === 'SELL'}
                          onChange={() => {
                            const updatedStrategies = [...strategies];
                            updatedStrategies[strategyIndex].direction = 'SELL';
                            setStrategies(updatedStrategies);
                          }}
                          />
                          SELL
                        </label>
                      </div>
                    </div>

                  <div className={styles.formGroup}>
                    <label>Total Contracts</label>
                      <input
                        type="number"
                        value={strategy.series.reduce((total, series) => total + (series.contracts || 1), 0)}
                        readOnly
                        style={{ backgroundColor: 'var(--color-bg-panel)', color: 'var(--color-text-muted)' }}
                        min="1"
                        max="100"
                      />
                    </div>
                </div>

                {/* Margin Calculator - Only show when security is selected */}
                {strategy.security && (
                  <MarginCalculator strategy={strategy} strategyIndex={strategyIndex} />
                )}

                <div className={styles.seriesSection}>
                  <h3>Series Configuration</h3>
                  {strategy.series.map((series, seriesIndex) => (
                    <div key={seriesIndex} className={styles.seriesGroup}>
                      <div className={styles.seriesHeader}>
                        <h4>Series {seriesIndex + 1}</h4>
                      </div>

                      <div className={styles.formRow} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <div className={styles.formGroup} style={{ flex: 1 }}>
                          <label>Contracts</label>
                          <input
                            type="number"
                            value={series.contracts || 1}
                            onChange={(e) => {
                              const updatedStrategies = [...strategies];
                              if (!updatedStrategies[strategyIndex].series[seriesIndex]) {
                                updatedStrategies[strategyIndex].series[seriesIndex] = {};
                              }
                              const value = e.target.value === '' ? 1 : parseInt(e.target.value) || 1;
                              updatedStrategies[strategyIndex].series[seriesIndex].contracts = value;
                              setStrategies(updatedStrategies);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                e.preventDefault();
                                const updatedStrategies = [...strategies];
                                if (!updatedStrategies[strategyIndex].series[seriesIndex]) {
                                  updatedStrategies[strategyIndex].series[seriesIndex] = {};
                                }
                                const currentValue = updatedStrategies[strategyIndex].series[seriesIndex].contracts || 1;
                                const newValue = e.key === 'ArrowUp' ? currentValue + 1 : Math.max(1, currentValue - 1);
                                updatedStrategies[strategyIndex].series[seriesIndex].contracts = newValue;
                                setStrategies(updatedStrategies);
                              }
                            }}
                            min="1"
                          />
                        </div>
                        <div className={styles.formGroup} style={{ flex: 1 }}>
                          <label>Take Profit (ticks)</label>
                          <input
                            type="number"
                            value={series.takeProfit || 10}
                            onChange={(e) => {
                              const updatedStrategies = [...strategies];
                              if (!updatedStrategies[strategyIndex].series[seriesIndex]) {
                                updatedStrategies[strategyIndex].series[seriesIndex] = {};
                              }
                              const value = e.target.value === '' ? 10 : parseInt(e.target.value) || 10;
                              updatedStrategies[strategyIndex].series[seriesIndex].takeProfit = value;
                              setStrategies(updatedStrategies);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                e.preventDefault();
                                const updatedStrategies = [...strategies];
                                if (!updatedStrategies[strategyIndex].series[seriesIndex]) {
                                  updatedStrategies[strategyIndex].series[seriesIndex] = {};
                                }
                                const currentValue = updatedStrategies[strategyIndex].series[seriesIndex].takeProfit || 10;
                                const newValue = e.key === 'ArrowUp' ? currentValue + 1 : Math.max(1, currentValue - 1);
                                updatedStrategies[strategyIndex].series[seriesIndex].takeProfit = newValue;
                                setStrategies(updatedStrategies);
                              }
                            }}
                            min="1"
                          />
                        </div>
                        <div className={styles.formGroup} style={{ flex: 1 }}>
                          <label>Stop Loss (ticks)</label>
                          <input
                            type="number"
                            value={series.stopLoss || 5}
                            onChange={(e) => {
                              const updatedStrategies = [...strategies];
                              if (!updatedStrategies[strategyIndex].series[seriesIndex]) {
                                updatedStrategies[strategyIndex].series[seriesIndex] = {};
                              }
                              const value = e.target.value === '' ? 5 : parseInt(e.target.value) || 5;
                              updatedStrategies[strategyIndex].series[seriesIndex].stopLoss = value;
                              setStrategies(updatedStrategies);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                e.preventDefault();
                                const updatedStrategies = [...strategies];
                                if (!updatedStrategies[strategyIndex].series[seriesIndex]) {
                                  updatedStrategies[strategyIndex].series[seriesIndex] = {};
                                }
                                const currentValue = updatedStrategies[strategyIndex].series[seriesIndex].stopLoss || 5;
                                const newValue = e.key === 'ArrowUp' ? currentValue + 1 : Math.max(1, currentValue - 1);
                                updatedStrategies[strategyIndex].series[seriesIndex].stopLoss = newValue;
                                setStrategies(updatedStrategies);
                              }
                            }}
                            min="1"
                          />
                        </div>
                        {seriesIndex > 0 && (
                          <div className={styles.formGroup} style={{ display: 'flex', alignItems: 'flex-end' }}>
                            <button
                              type="button"
                              onClick={() => removeSeries(strategyIndex, seriesIndex)}
                              className={styles.removeSeriesButton}
                              style={{ 
                                background: 'var(--color-danger)', 
                                border: '1px solid var(--color-danger)', 
                                color: 'var(--color-text)', 
                                padding: '8px', 
                                borderRadius: '4px', 
                                cursor: 'pointer',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '38px',
                                width: '38px'
                              }}
                              title="Remove this series"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {strategy.series.length < 5 && (
                    <button
                      type="button"
                      onClick={() => addSeries(strategyIndex)}
                      className={styles.addSeriesButton}
                    >
                      <FaPlus /> Add Series
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          <div className={styles.formActions}>
            <button
              type="button"
                      onClick={() => setShowPreview(true)}
              className={styles.previewButton}
                    >
              Preview Strategies
                    </button>
                    <button
                      type="submit"
              className={styles.submitButton}
                    >
              Save Strategies
                    </button>
                    <button
                      type="button"
                      onClick={() => reset()}
              className={styles.resetButton}
                    >
                      Reset
                    </button>
        </div>
      </Form>

      {showPreview && (
          <div className={styles.previewModal}>
            <div className={styles.previewContent}>
              <h2>Strategy Preview</h2>
              <pre>{JSON.stringify(strategies, null, 2)}</pre>
              <button onClick={() => setShowPreview(false)}>Close</button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editModalOpen && (
        <div className={styles.previewModal}>
          <div className={styles.previewContent} style={{ maxWidth: '800px', width: '90%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className={styles.strategyHeader}>
              <h2>Edit Strategy</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  className={styles.submitButton}
                  disabled={loading}
                  style={{ minWidth: 90 }}
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditModalOpen(false)}
                  className={styles.resetButton}
                  style={{ minWidth: 90 }}
                >
                  Cancel
                </button>
              </div>
            </div>

            {strategyToEdit && (
              <div className={styles.strategyContent}>
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Strategy Name</label>
                    <input
                      type="text"
                      value={editForm.name || ''}
                      onChange={e => handleEditFormChange('name', e.target.value)}
                      placeholder="Enter strategy name"
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label>Time Zone</label>
                    <Select
                      classNamePrefix="select"
                      options={TIMEZONES}
                      value={TIMEZONES.find(tz => tz.value === editForm.timeZone)}
                      onChange={option => handleEditFormChange('timeZone', option.value)}
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Execution Days</label>
                    <Select
                      classNamePrefix="select"
                      options={DAYS}
                      isMulti
                      value={DAYS.filter(day => {
                        const executionDays = editForm.executionDays || [];
                        return executionDays.includes(day.value);
                      })}
                      onChange={options => {
                        handleEditFormChange('executionDays', options.map(opt => opt.value));
                      }}
                    />
                  </div>

                  <div className={styles.formGroup}>
                    <label>Execution Start Date</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={editForm.executionStartDate ? format(new Date(editForm.executionStartDate), 'yyyy-MM-dd') : ''}
                      onFocus={() => {
                        setEditCalendarOpen({ ...editCalendarOpen, start: true });
                      }}
                      onChange={e => {
                        const parsed = parse(e.target.value, 'yyyy-MM-dd', new Date());
                        if (!isNaN(parsed)) {
                          handleEditFormChange('executionStartDate', parsed);
                        }
                      }}
                      placeholder="YYYY-MM-DD"
                      readOnly={false}
                      style={{ background: 'var(--color-bg-alt)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 4, padding: '0.75rem', fontSize: '1rem' }}
                    />
                    {editCalendarOpen.start && (
                      <div style={{ position: 'absolute', zIndex: 10, background: 'var(--color-bg-panel)', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                        <DayPicker
                          mode="single"
                          selected={editForm.executionStartDate ? new Date(editForm.executionStartDate) : undefined}
                          onSelect={date => {
                            setEditCalendarOpen({ ...editCalendarOpen, start: false });
                            if (date) {
                              handleEditFormChange('executionStartDate', date);
                            }
                          }}
                          fromDate={new Date()}
                          styles={{
                            caption: { color: 'var(--color-text)' },
                            head_cell: { color: 'var(--color-text)' },
                            day_selected: { backgroundColor: 'var(--color-success)', color: 'var(--color-text-strong)' },
                            day: { color: 'var(--color-text)', background: 'transparent' },
                            months: { background: 'var(--color-bg-panel)', borderRadius: 8 },
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label>Execution End Date</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={editForm.executionEndDate ? format(new Date(editForm.executionEndDate), 'yyyy-MM-dd') : ''}
                      onFocus={() => {
                        setEditCalendarOpen({ ...editCalendarOpen, end: true });
                      }}
                      onChange={e => {
                        const parsed = parse(e.target.value, 'yyyy-MM-dd', new Date());
                        if (!isNaN(parsed)) {
                          handleEditFormChange('executionEndDate', parsed);
                        }
                      }}
                      placeholder="YYYY-MM-DD"
                      readOnly={false}
                      style={{ background: 'var(--color-bg-alt)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 4, padding: '0.75rem', fontSize: '1rem' }}
                    />
                    {editCalendarOpen.end && (
                      <div style={{ position: 'absolute', zIndex: 10, background: 'var(--color-bg-panel)', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>
                        <DayPicker
                          mode="single"
                          selected={editForm.executionEndDate ? new Date(editForm.executionEndDate) : undefined}
                          onSelect={date => {
                            setEditCalendarOpen({ ...editCalendarOpen, end: false });
                            if (date) {
                              handleEditFormChange('executionEndDate', date);
                            }
                          }}
                          fromDate={editForm.executionStartDate ? new Date(editForm.executionStartDate) : new Date()}
                          styles={{
                            caption: { color: 'var(--color-text)' },
                            head_cell: { color: 'var(--color-text)' },
                            day_selected: { backgroundColor: 'var(--color-success)', color: 'var(--color-text-strong)' },
                            day: { color: 'var(--color-text)', background: 'transparent' },
                            months: { background: 'var(--color-bg-panel)', borderRadius: 8 },
                          }}
                        />
                      </div>
                    )}
                  </div>

                  <div className={styles.formGroup}>
                    <label>Execution Time</label>
                    <input
                      type="text"
                      className={styles.input}
                      value={editForm.executionTime || ''}
                      onChange={e => {
                        console.log('Updating execution time to:', e.target.value);
                        handleEditFormChange('executionTime', e.target.value);
                      }}
                      placeholder="HH:mm:ss"
                      style={{ background: 'var(--color-bg-alt)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: 4, padding: '0.75rem', fontSize: '1rem' }}
                    />
                  </div>
                </div>

                <div className={styles.formRow} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <div className={styles.formGroup} style={{ flex: 1 }}>
                    <label>Security</label>
                    <Select
                      classNamePrefix="select"
                      options={SECURITIES}
                      value={SECURITIES.find(sec => sec.value === editForm.security)}
                      onChange={option => handleEditFormChange('security', option.value)}
                    />
                  </div>
                  <div className={styles.formGroup} style={{ flex: 1 }}>
                    <label>Direction</label>
                    <div className={styles.radioGroup}>
                      <label>
                        <input
                          type="radio"
                          checked={editForm.direction === 'BUY'}
                          onChange={() => handleEditFormChange('direction', 'BUY')}
                        />
                        BUY
                      </label>
                      <label>
                        <input
                          type="radio"
                          checked={editForm.direction === 'SELL'}
                          onChange={() => handleEditFormChange('direction', 'SELL')}
                        />
                        SELL
                      </label>
                    </div>
                  </div>
                  <div className={styles.formGroup} style={{ flex: 1 }}>
                    <label>Total Contracts</label>
                    <input
                      type="number"
                      value={editForm.series.reduce((total, series) => total + (series.contracts || 1), 0)}
                      readOnly
                      style={{ backgroundColor: 'var(--color-bg-panel)', color: 'var(--color-text-muted)' }}
                      min="1"
                      max="100"
                    />
                  </div>
                </div>

                {/* Margin Calculator - Only show when security is selected */}
                {editForm.security && (
                  <MarginCalculator strategy={editForm} strategyIndex={0} />
                )}

                {/* Series Configuration */}
                <div className={styles.seriesSection}>
                  <h3>Series Configuration</h3>
                  {editForm.series && editForm.series.map((series, seriesIndex) => (
                    <div key={seriesIndex} className={styles.seriesGroup}>
                      <div className={styles.seriesHeader}>
                        <h4>Series {seriesIndex + 1}</h4>
                      </div>

                      <div className={styles.formRow} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <div className={styles.formGroup} style={{ flex: 1 }}>
                          <label>Contracts</label>
                          <input
                            type="number"
                            value={series.contracts || 1}
                            onChange={(e) => {
                              const updatedSeries = [...(editForm.series || [])];
                              if (!updatedSeries[seriesIndex]) {
                                updatedSeries[seriesIndex] = {};
                              }
                              const value = e.target.value === '' ? 1 : parseInt(e.target.value) || 1;
                              updatedSeries[seriesIndex].contracts = value;
                              handleEditFormChange('series', updatedSeries);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                e.preventDefault();
                                const updatedSeries = [...(editForm.series || [])];
                                if (!updatedSeries[seriesIndex]) {
                                  updatedSeries[seriesIndex] = {};
                                }
                                const currentValue = updatedSeries[seriesIndex].contracts || 1;
                                const newValue = e.key === 'ArrowUp' ? currentValue + 1 : Math.max(1, currentValue - 1);
                                updatedSeries[seriesIndex].contracts = newValue;
                                handleEditFormChange('series', updatedSeries);
                              }
                            }}
                            min="1"
                          />
                        </div>
                        <div className={styles.formGroup} style={{ flex: 1 }}>
                          <label>Take Profit (ticks)</label>
                          <input
                            type="number"
                            value={series.takeProfit || 10}
                            onChange={(e) => {
                              const updatedSeries = [...(editForm.series || [])];
                              if (!updatedSeries[seriesIndex]) {
                                updatedSeries[seriesIndex] = {};
                              }
                              const value = e.target.value === '' ? 10 : parseInt(e.target.value) || 10;
                              updatedSeries[seriesIndex].takeProfit = value;
                              handleEditFormChange('series', updatedSeries);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                e.preventDefault();
                                const updatedSeries = [...(editForm.series || [])];
                                if (!updatedSeries[seriesIndex]) {
                                  updatedSeries[seriesIndex] = {};
                                }
                                const currentValue = updatedSeries[seriesIndex].takeProfit || 10;
                                const newValue = e.key === 'ArrowUp' ? currentValue + 1 : Math.max(1, currentValue - 1);
                                updatedSeries[seriesIndex].takeProfit = newValue;
                                handleEditFormChange('series', updatedSeries);
                              }
                            }}
                            min="1"
                          />
                        </div>
                        <div className={styles.formGroup} style={{ flex: 1 }}>
                          <label>Stop Loss (ticks)</label>
                          <input
                            type="number"
                            value={series.stopLoss || 5}
                            onChange={(e) => {
                              const updatedSeries = [...(editForm.series || [])];
                              if (!updatedSeries[seriesIndex]) {
                                updatedSeries[seriesIndex] = {};
                              }
                              const value = e.target.value === '' ? 5 : parseInt(e.target.value) || 5;
                              updatedSeries[seriesIndex].stopLoss = value;
                              handleEditFormChange('series', updatedSeries);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                e.preventDefault();
                                const updatedSeries = [...(editForm.series || [])];
                                if (!updatedSeries[seriesIndex]) {
                                  updatedSeries[seriesIndex] = {};
                                }
                                const currentValue = updatedSeries[seriesIndex].stopLoss || 5;
                                const newValue = e.key === 'ArrowUp' ? currentValue + 1 : Math.max(1, currentValue - 1);
                                updatedSeries[seriesIndex].stopLoss = newValue;
                                handleEditFormChange('series', updatedSeries);
                              }
                            }}
                            min="1"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cleanup Confirmation Modal */}
      {cleanupModalOpen && (
        <div className={styles.previewModal}>
          <div className={styles.previewContent} style={{ maxWidth: '600px', width: '90%' }}>
            <div className={styles.strategyHeader}>
              <h2 style={{ color: 'var(--color-warning)', marginBottom: '20px' }}>
                <FaBroom style={{ marginRight: '10px' }} />
                Cleanup Confirmation
              </h2>
            </div>
            
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ color: 'var(--color-text)', marginBottom: '15px' }}>⚠️ Important: Please Read Carefully</h3>
              
              <div style={{ background: 'rgba(255, 193, 7, 0.1)', border: '1px solid var(--color-warning)', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
                <h4 style={{ color: 'var(--color-warning)', marginBottom: '15px' }}>What this cleanup does:</h4>
                <ul style={{ color: 'var(--color-text)', lineHeight: '1.6', margin: 0, paddingLeft: '20px' }}>
                  <li><strong>Removes orphaned scheduled jobs</strong> - Jobs that exist in the scheduler but have no corresponding strategy in the database</li>
                  <li><strong>Removes orphaned strategies</strong> - Strategies that exist in the database but have no corresponding scheduled job</li>
                  <li><strong>Scope limited to your account only</strong> - Only affects strategies and jobs belonging to your user account</li>
                  <li><strong>Cannot be undone</strong> - This action is permanent and irreversible</li>
                </ul>
              </div>
              
              <div style={{ background: 'rgba(0, 204, 0, 0.1)', border: '1px solid var(--color-success)', borderRadius: '8px', padding: '20px' }}>
                <h4 style={{ color: 'var(--color-success)', marginBottom: '15px' }}>What this cleanup does NOT do:</h4>
                <ul style={{ color: 'var(--color-text)', lineHeight: '1.6', margin: 0, paddingLeft: '20px' }}>
                  <li>Does not affect active, properly configured strategies</li>
                  <li>Does not affect other users' strategies or jobs</li>
                  <li>Does not delete your trading history or account data</li>
                  <li>Does not affect strategies that are currently running or scheduled</li>
                </ul>
              </div>
            </div>
            
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                type="button"
                onClick={() => setCleanupModalOpen(false)}
                className={styles.resetButton}
                style={{ minWidth: '100px' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCleanupConfirm}
                className={styles.submitButton}
                disabled={loading}
                style={{ 
                  minWidth: '100px',
                  background: 'var(--color-warning)',
                  border: '1px solid var(--color-warning)',
                  color: 'var(--color-text-strong)',
                  fontWeight: 'bold'
                }}
              >
                {loading ? 'Cleaning...' : 'Proceed with Cleanup'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Container>
    </LocalizationProvider>
  );
};

export default TradingStrategyConfig; 